import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FilterState } from "@/components/AdvancedFilters";

// Swedish synonym mapping for search expansion
const SEARCH_SYNONYMS: Record<string, { types?: string[]; categories?: string[]; terms?: string[] }> = {
  poster: { types: ["print-on-demand", "print"], categories: ["illustration"] },
  posters: { types: ["print-on-demand", "print"], categories: ["illustration"] },
  konst: { categories: ["målning", "illustration", "skulptur", "fotografi"] },
  tavla: { categories: ["målning"], terms: ["målning"] },
  tavlor: { categories: ["målning"], terms: ["målning"] },
  tryck: { types: ["print", "print-on-demand"] },
  print: { types: ["print", "print-on-demand"] },
  original: { types: ["original"] },
  originalmålning: { types: ["original"], categories: ["målning"] },
  foto: { categories: ["fotografi"] },
  fotografi: { categories: ["fotografi"] },
  illustration: { categories: ["illustration"] },
  skulptur: { categories: ["skulptur"] },
  målning: { categories: ["målning"] },
  akvarell: { terms: ["akvarell"], categories: ["målning"] },
  olja: { terms: ["olja"], categories: ["målning"] },
  abstrakt: { terms: ["abstrakt"] },
  landskap: { terms: ["landskap"] },
};

const ITEMS_PER_PAGE = 20;

export function useExploreArtworks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const qParam = searchParams.get("q") || searchParams.get("search") || "";
  const categoryParam = searchParams.get("category");
  
  const [searchText, setSearchText] = useState<string>(qParam);
  const [category, setCategory] = useState<string>(categoryParam || "all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [artworkType, setArtworkType] = useState<"all" | "original" | "print">("all");
  const [selectedArtist, setSelectedArtist] = useState<string>("all");
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({
    priceRange: [0, 50000],
    technique: "all",
    color: "all"
  });

  // Sync searchText with URL param
  useEffect(() => {
    setSearchText(qParam);
  }, [qParam]);

  // Sync category with URL param
  useEffect(() => {
    if (categoryParam) setCategory(categoryParam);
  }, [categoryParam]);

  useEffect(() => {
    if (typeParam === "original") {
      setArtworkType("original");
    } else if (typeParam === "print") {
      setArtworkType("print");
    } else {
      setArtworkType("all");
    }
  }, [typeParam]);

  const { data: filterData } = useQuery({
    queryKey: ["explore-filter-data"],
    queryFn: async () => {
      // Fetch artworks to get available categories and artist IDs
      const { data: artworksData, error: artworksError } = await supabase
        .from("artworks")
        .select("artist_id, category")
        .eq("is_active", true)
        .eq("approval_status", "approved");
      
      if (artworksError) throw artworksError;
      
      // Get unique artist IDs and categories
      const artistIds = [...new Set((artworksData || []).map(a => a.artist_id))];
      const availableCategories = [...new Set((artworksData || []).map(a => a.category).filter(Boolean))];
      
      if (artistIds.length === 0) return { artists: [], availableCategories: [] };
      
      // Fetch only those artists
      const { data: artistsData, error } = await supabase
        .from("public_profiles")
        .select("id, username, full_name")
        .eq("user_type", "artist")
        .eq("is_active", true)
        .in("id", artistIds)
        .order("username");
      
      if (error) throw error;
      return { artists: artistsData || [], availableCategories };
    },
  });

  const artists = filterData?.artists;
  const availableCategories = filterData?.availableCategories || [];

  const { 
    data: artworksData, 
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ["artworks", category, sortBy, artworkType, selectedArtist, advancedFilters, searchText],
    queryFn: async ({ pageParam = 0 }) => {
      const from = pageParam * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      // Build search filter parts
      let searchOrParts: string[] = [];

      if (searchText && searchText.trim().length > 0) {
        const term = searchText.trim();
        const termLower = term.toLowerCase();

        // 1. Always search text fields
        searchOrParts.push(
          `title.ilike.%${term}%`,
          `description.ilike.%${term}%`,
          `category.ilike.%${term}%`,
          `technique.ilike.%${term}%`
        );

        // 2. Expand synonyms
        const synonym = SEARCH_SYNONYMS[termLower];
        if (synonym) {
          if (synonym.types) {
            synonym.types.forEach(t => searchOrParts.push(`artwork_type.eq.${t}`));
          }
          if (synonym.categories) {
            synonym.categories.forEach(c => searchOrParts.push(`category.ilike.%${c}%`));
          }
          if (synonym.terms) {
            synonym.terms.forEach(t => {
              searchOrParts.push(`title.ilike.%${t}%`, `description.ilike.%${t}%`, `technique.ilike.%${t}%`);
            });
          }
        }

        // 3. Search artist names
        const { data: matchingArtists } = await supabase
          .from("public_profiles")
          .select("id")
          .or(`full_name.ilike.%${term}%,username.ilike.%${term}%`);

        if (matchingArtists && matchingArtists.length > 0) {
          const ids = matchingArtists.map(a => a.id).join(",");
          searchOrParts.push(`artist_id.in.(${ids})`);
        }
      }

      let query = supabase
        .from("artworks")
        .select(`*, artwork_images (image_url, display_order, is_room_mockup)`, { count: 'exact' })
        .eq("is_active", true)
        .eq("approval_status", "approved")
        .range(from, to);

      if (searchOrParts.length > 0) {
        // Deduplicate
        const unique = [...new Set(searchOrParts)];
        query = query.or(unique.join(","));
      }
      if (category !== "all") query = query.eq("category", category);
      if (artworkType === "print") query = query.in("artwork_type", ["print", "print-on-demand"]);
      else if (artworkType === "original") query = query.eq("artwork_type", "original");
      if (selectedArtist !== "all") query = query.eq("artist_id", selectedArtist);


      if (advancedFilters.priceRange[0] > 0) query = query.gte("price", advancedFilters.priceRange[0]);
      if (advancedFilters.priceRange[1] < 50000) query = query.lte("price", advancedFilters.priceRange[1]);
      if (advancedFilters.technique !== "all") query = query.eq("technique", advancedFilters.technique);
      if (advancedFilters.color !== "all") query = query.eq("dominant_color", advancedFilters.color);

      if (sortBy === "newest") query = query.order("created_at", { ascending: false });
      else if (sortBy === "price_low") query = query.order("price", { ascending: true });
      else if (sortBy === "price_high") query = query.order("price", { ascending: false });

      const { data: arts, error, count } = await query;
      if (error) throw error;

      if (sortBy === "popular") {
        const artworkIds = (arts || []).map((a: any) => a.id);
        if (artworkIds.length > 0) {
          const { data: salesData } = await supabase
            .from("order_items")
            .select("artwork_id, quantity, order_id")
            .in("artwork_id", artworkIds);

          const orderIds = Array.from(new Set((salesData || []).map(item => item.order_id)));
          const { data: completedOrders } = await supabase
            .from("orders")
            .select("id")
            .in("id", orderIds)
            .eq("status", "delivered");

          const completedOrderIds = new Set((completedOrders || []).map(o => o.id));
          const salesMap = new Map<string, number>();
          (salesData || []).forEach((item: any) => {
            if (completedOrderIds.has(item.order_id)) {
              const current = salesMap.get(item.artwork_id) || 0;
              salesMap.set(item.artwork_id, current + (item.quantity || 1));
            }
          });

          (arts || []).forEach((art: any) => {
            art.sales_count = salesMap.get(art.id) || 0;
          });
          arts?.sort((a: any, b: any) => (b.sales_count || 0) - (a.sales_count || 0));
        }
      }

      const artistIds = Array.from(new Set((arts || []).map((a: any) => a.artist_id).filter(Boolean)));
      let profilesMap = new Map<string, any>();
      if (artistIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, full_name, avatar_url")
          .in("id", artistIds);
        (profiles || []).forEach((p: any) => profilesMap.set(p.id, p));
      }

      // Fetch min variant prices for print-on-demand artworks
      const podArtworkIds = (arts || [])
        .filter((a: any) => a.artwork_type === 'print-on-demand')
        .map((a: any) => a.id);

      let minPricesMap = new Map<string, number>();
      if (podArtworkIds.length > 0) {
        const { data: variants } = await supabase
          .from('artwork_variants')
          .select('artwork_id, price')
          .in('artwork_id', podArtworkIds)
          .eq('is_active', true);
        
        (variants || []).forEach((v: any) => {
          const current = minPricesMap.get(v.artwork_id);
          if (!current || v.price < current) {
            minPricesMap.set(v.artwork_id, v.price);
          }
        });
      }

      const enriched = (arts || []).map((a: any) => ({
        ...a,
        public_profiles: profilesMap.get(a.artist_id) || null,
        min_variant_price: a.artwork_type === 'print-on-demand' 
          ? minPricesMap.get(a.id) || a.price
          : null,
      }));

      return { artworks: enriched, total: count || 0 };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.artworks.length, 0);
      return loadedCount < lastPage.total ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });

  const artworks = artworksData?.pages.flatMap(page => page.artworks) || [];
  const totalCount = artworksData?.pages[0]?.total || 0;

  const filteredArtworks = artworks.filter((artwork) => {
    if (advancedFilters.minWidth && artwork.width_cm < advancedFilters.minWidth) return false;
    if (advancedFilters.maxWidth && artwork.width_cm > advancedFilters.maxWidth) return false;
    if (advancedFilters.minHeight && artwork.height_cm < advancedFilters.minHeight) return false;
    if (advancedFilters.maxHeight && artwork.height_cm > advancedFilters.maxHeight) return false;
    return true;
  });

  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const updateSearchText = (text: string) => {
    setSearchText(text);
    const newParams = new URLSearchParams(searchParams);
    if (text) {
      newParams.set("q", text);
    } else {
      newParams.delete("q");
      newParams.delete("search");
    }
    setSearchParams(newParams, { replace: true });
  };

  return {
    searchText,
    setSearchText: updateSearchText,
    category,
    setCategory,
    sortBy,
    setSortBy,
    artworkType,
    setArtworkType,
    selectedArtist,
    setSelectedArtist,
    advancedFilters,
    setAdvancedFilters,
    artists,
    availableCategories,
    filteredArtworks,
    totalCount,
    isLoading,
    isFetchingNextPage,
    observerTarget,
  };
}
